import React, { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck, AlertTriangle, FileDown, Info, ShieldAlert } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const ICONS = { alert: AlertTriangle, alert_recovered: CheckCheck, report: FileDown, security: ShieldAlert };

const TABS = [
  { key: "all", label: "All" },
  { key: "security", label: "Security" },
];

export function NotificationBell() {
  const [count, setCount] = useState(0);
  const [securityCount, setSecurityCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then(({ data }) => setCount(data.count)).catch(() => {});
    // Independent security-only unread count so the tab badge doesn't
    // under-count when there are >20 notifications in the feed.
    api.get("/notifications/unread-count", { params: { type: "security" } })
      .then(({ data }) => setSecurityCount(data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  // Fetch the current tab's slice from the server so the "Security" tab isn't
  // blank when the newest 20 items happen to be non-security.
  useEffect(() => {
    if (!open) return;
    const params = tab === "all" ? { limit: 20 } : { limit: 20, type: tab };
    api.get("/notifications", { params })
      .then(({ data }) => setItems(data))
      .catch(() => {});
  }, [open, tab]);

  const onOpenChange = (v) => {
    setOpen(v);
    // Reset to All when reopening so the user's default view is unfiltered.
    if (v) setTab("all");
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      setCount(0);
      setSecurityCount(0);
      setItems((l) => l.map((n) => ({ ...n, read: true })));
    } catch (e) {
      console.warn("Failed to mark notifications read", e);
    }
  };

  const download = async (n) => {
    const fileId = n.meta?.file_id;
    if (!fileId) return;
    try {
      const res = await api.get(`/reports/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = (res.headers["content-disposition"] || "").split('filename="')[1]?.replace('"', "") || "report";
      a.click(); URL.revokeObjectURL(url);
    } catch {
      /* download URL revoked / stale — no-op */
    }
  };

  // Server already filters; the client-side pass is a defensive no-op that
  // keeps behaviour correct if a future tab predicate needs local filtering.
  const filtered = tab === "all" ? items : items.filter((n) => n.type === tab);

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative w-9 h-9 rounded-xl flex items-center justify-center text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors">
          <Bell className="w-[18px] h-[18px]" />
          {count > 0 && (
            <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--danger)] text-white text-[10px] font-semibold flex items-center justify-center">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--glass-border)]">
          <span className="text-sm font-medium text-[color:var(--ink)]">Notifications</span>
          <Button data-testid="mark-all-read" size="sm" variant="ghost" className="h-7 text-xs text-[color:var(--brand-ocean)]" onClick={markAll}>
            <CheckCheck className="w-3.5 h-3.5 mr-1" />Mark all read
          </Button>
        </div>
        <div className="flex gap-1 px-3 py-1.5 border-b border-[color:var(--glass-border)]" data-testid="notification-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const badge = t.key === "security" && securityCount > 0 ? securityCount : null;
            return (
              <button
                key={t.key}
                data-testid={`notification-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]"
                    : "border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]"
                }`}
              >
                {t.key === "security" && <ShieldAlert className="w-3 h-3" />}
                {t.label}
                {badge !== null && (
                  <span className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 ${active ? "bg-white/25 text-white" : "bg-[color:var(--danger)] text-white"}`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div data-testid="notification-empty" className="py-8 text-center text-xs text-[color:var(--ink-3)]">
              {tab === "security" ? "No security events yet" : "No notifications yet"}
            </div>
          ) : filtered.map((n) => {
            const Icon = ICONS[n.type] || Info;
            const iconColor = n.type === "alert" || n.type === "security"
              ? "text-[color:var(--danger)]"
              : "text-[color:var(--brand-ocean)]";
            return (
              <div key={n.id} data-testid="notification-item" data-notif-type={n.type}
                   className={"px-3 py-2.5 border-b border-[color:var(--glass-border)] last:border-0 " + (n.read ? "opacity-60" : "bg-[color:rgba(43,78,255,0.05)]")}>
                <div className="flex gap-2.5">
                  <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + iconColor} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-[color:var(--ink)] leading-snug">{n.title}</div>
                    <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5 leading-relaxed">{n.body}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-[color:var(--ink-3)]">{new Date(n.created_at).toLocaleString()}</span>
                      {n.type === "report" && n.meta?.file_id && (
                        <button data-testid="notification-download" onClick={() => download(n)} className="text-[10px] text-[color:var(--brand-ocean)] font-medium hover:underline">Download</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
